import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { nachAktivierung } from "@/lib/prerendering";
import { getAppVersion, getPlatform } from "@/lib/platform";
import type {
  AppWelcomeAktion,
  FotoQuelle,
  KatalogFelder,
  KatalogKategorie,
  KatalogName,
  PushFrageQuelle,
  RueckkehrQuelle,
  RueckkehrStufe,
  ScreenKey,
  ScreenQuelle,
  WidgetGroesse,
  WidgetZiel,
} from "@/lib/tracking/katalog";
import { abschlussAnfrage, Vordergrunduhr, type AbschlussEingabe } from "@/lib/tracking/abschluss";

// Nutzungsmessung App vs. Web (Roadmap 1.11/1.12, 04.10.2026): Jeder Seitenaufruf, jede
// Sitzung und jedes Ereignis trägt die Plattform (ios/android/web) und in der App die
// App-Version. Nur eigene Datenbank, nur eingeloggte Kunden, keine Fremd-Tracker,
// keine Nachrichteninhalte.
async function plattformFelder(): Promise<{ platform: string; app_version: string | null }> {
  return { platform: getPlatform(), app_version: await getAppVersion() };
}

// Vorab gerenderte Seiten (Speculation Rules, Paket 1d): page_views und
// user_sessions zählen Aufrufe eingeloggter Kunden. Beide Inserts warten auf die
// Aktivierung, sonst steht in page_views ein Aufruf mit Verweildauer, den nie
// jemand gesehen hat.

// --- Sitzungskennung nur im Speicher der Seite (Datenschutz, Noah 08.10.2026) ---
// Bis 08.10. lag die Kennung als `cc_session_id` im sessionStorage. § 25 TDDDG erfasst
// jedes Speichern auf dem Gerät, deshalb speichert die Messung jetzt NICHTS mehr dort:
// Die Kennung entsteht beim Laden der Seite bzw. beim Start der App und lebt nur in
// dieser Variable. Neu laden, neuer Tab oder App neu gestartet = neue Sitzung.
// Folge für die Zahlen: Ein vollständiges Neuladen (z. B. Rückkehr von Stripe) zählt
// jetzt als eigene Sitzung. Brain-Kennzahlen zählen Kunden (distinct user_id) und
// sind davon nicht betroffen; nur Sitzungszahlen im Website-Traffic steigen leicht.
const SITZUNG_ID = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

function getSessionId(): string {
  return SITZUNG_ID;
}

// --- Device Type ---
function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

// --- Get current user id (fire-and-forget friendly) ---
async function getUserId(): Promise<string | null> {
  try {
    // getSession liest den lokal gespeicherten Login (kein Netzwerk-Aufruf). Die Datenbank
    // prüft das Token bei jedem Insert ohnehin selbst (RLS: user_id = auth.uid()).
    const { data } = await supabase.auth.getSession();
    return data.session?.user?.id ?? null;
  } catch {
    return null;
  }
}

// --- Abschluss von Sitzung und Seitenaufruf mit Nutzer-Token (Tracking T0, 09.10.2026) ---
// Das Token wird nur im Arbeitsspeicher mitgelesen (onAuthStateChange liefert es beim
// Anmelden, Erneuern und sofort für den aktuellen Stand). So kann der PATCH beim Verlassen
// synchron rausgehen; ein await vor fetch würde beim Schließen der Seite oft nicht mehr laufen.
let zugangsToken: string | null = null;
let tokenBeobachtet = false;

function tokenBeobachten(): void {
  if (tokenBeobachtet) return;
  tokenBeobachtet = true;
  supabase.auth.onAuthStateChange((_ereignis, sitzung) => {
    zugangsToken = sitzung?.access_token ?? null;
  });
}

function abschlussSenden(eingabe: Omit<AbschlussEingabe, "basisUrl" | "apikey" | "token">): void {
  const anfrage = abschlussAnfrage({
    ...eingabe,
    basisUrl: import.meta.env.VITE_SUPABASE_URL,
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    token: zugangsToken,
  });
  if (!anfrage) return;
  try {
    fetch(anfrage.url, anfrage.init).catch(() => {});
  } catch {
    // Messung darf nie stören.
  }
}

function seiteSichtbar(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

// --- Sammler: alle Ereignisse im Arbeitsspeicher, gebündelt verschickt (Tracking-Plan T1) ---
// Jedes Ereignis landet zuerst in einem Puffer im Arbeitsspeicher (max. 50, ältere fallen raus)
// und geht alle 10 s bzw. beim Wechsel in den Hintergrund / pagehide als EIN Insert raus,
// per fetch keepalive mit dem Token des Nutzers (überlebt Seitenwechsel, z. B. zu Stripe).
// Nichts landet auf dem Gerät (kein localStorage/IndexedDB, § 25 TDDDG, Entscheidung 08.10.):
// App geschlossen während offline = Ereignisse verloren. Akzeptiert (Scope §3.6).
// Buchungsereignisse gehen sofort raus (wenige, wichtig, oft direkt vor einer Weiterleitung).
interface PufferZeile {
  event_name: string;
  event_category: string;
  event_data: Record<string, unknown>;
  page_path: string;
  created_at: string;
}

const PUFFER_MAX = 50;
const PUFFER_TAKT_MS = 10_000;
let puffer: PufferZeile[] = [];
let pufferTimer: ReturnType<typeof setTimeout> | null = null;
let sammlerAktiv = false;
// Login nur im Arbeitsspeicher gemerkt (aus dem Supabase-Client), damit pagehide synchron senden kann.
let anmeldung: { userId: string; token: string } | null = null;

async function anmeldungAktualisieren(): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    const s = data.session;
    anmeldung = s?.user?.id && s.access_token ? { userId: s.user.id, token: s.access_token } : null;
  } catch {
    /* bleibt beim letzten Stand */
  }
}

async function pufferSenden(): Promise<void> {
  if (pufferTimer) { clearTimeout(pufferTimer); pufferTimer = null; }
  if (puffer.length === 0) return;
  const zeilen = puffer;
  puffer = [];
  try {
    if (!anmeldung) await anmeldungAktualisieren();
    const a = anmeldung;
    if (!a) return; // nur eingeloggte Nutzer
    const felder = await plattformFelder();
    const body = JSON.stringify(zeilen.map((z) => ({ user_id: a.userId, session_id: getSessionId(), ...felder, ...z })));
    fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${a.token}`,
        Prefer: "return=minimal",
      },
      body,
      // keepalive erlaubt höchstens 64 KB; 50 kleine Zeilen liegen weit darunter.
      keepalive: body.length < 60_000,
    }).then((r) => { if (r.status === 401) anmeldung = null; }).catch(() => {});
  } catch (e) {
    console.error("[Tracking] Versand fehlgeschlagen:", e);
  }
}

function sammlerStarten(): void {
  if (sammlerAktiv || typeof document === "undefined") return;
  sammlerAktiv = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      screensImHintergrund();
      void pufferSenden();
    } else {
      screensZurueck();
    }
  });
  window.addEventListener("pagehide", () => {
    screensImHintergrund();
    void pufferSenden();
  });
}

function ereignisMerken(eventName: string, category: string, eventData: Record<string, unknown>): void {
  sammlerStarten();
  // Aktueller Screen automatisch dazu (Scope §3.4), außer das Ereignis bringt ihn selbst mit.
  const oben = screenStapel[screenStapel.length - 1];
  const data = oben && !("screen" in eventData) ? { ...eventData, screen: oben.screen } : eventData;
  puffer.push({
    event_name: eventName,
    event_category: category,
    event_data: data,
    page_path: window.location.pathname,
    created_at: new Date().toISOString(),
  });
  if (puffer.length > PUFFER_MAX) puffer = puffer.slice(-PUFFER_MAX);
  void anmeldungAktualisieren();
  if (category === "booking") { void pufferSenden(); return; }
  if (!pufferTimer) pufferTimer = setTimeout(() => void pufferSenden(), PUFFER_TAKT_MS);
}

// --- trackEvent (exported standalone) ---
/**
 * Ereignis messen. Nur Namen, Kategorien und Felder aus dem Katalog (src/lib/tracking/katalog.ts),
 * der Typ erzwingt das, der Wächter scripts/check-tracking-katalog.mjs zusätzlich im CI.
 * Fire-and-forget: wirft nie, wartet nie auf das Netz.
 */
export async function trackEvent<N extends KatalogName>(
  eventName: N,
  category: KatalogKategorie<N>,
  eventData: KatalogFelder<N>,
): Promise<void> {
  try {
    ereignisMerken(eventName, category, eventData as Record<string, unknown>);
  } catch (e) {
    console.error("[Tracking] trackEvent error:", e);
  }
}

// --- Predefined TrackingEvents ---
// Felder einheitlich snake_case (T1, 09.10.2026): dog_id statt dogId, booking_id statt bookingId,
// photo_id statt photoId, schritt statt step. Alte Zeilen bildet die Sicht tracking_kunden_events ab.
export const TrackingEvents = {
  bookingStarted: (dogId?: string) =>
    trackEvent("booking_started", "booking", { dog_id: dogId }),
  bookingCompleted: (bookingId: string, dogId?: string) =>
    trackEvent("booking_completed", "booking", { booking_id: bookingId, dog_id: dogId }),
  /** Ohne Grund-Freitext (Katalog: keine Freitexte). */
  bookingCancelled: (bookingId: string) =>
    trackEvent("booking_cancelled", "booking", { booking_id: bookingId }),
  photoViewed: (photoId: string, extra?: { dog_id?: string; quelle?: FotoQuelle }) =>
    trackEvent("photo_viewed", "engagement", { photo_id: photoId, ...(extra ?? {}) }),
  dogProfileViewed: (dogId: string) =>
    trackEvent("dog_profile_viewed", "engagement", { dog_id: dogId }),
  // App-Nutzung (Roadmap 1.11/1.12). Nur Ereignisname und kleiner Kontext, keine Inhalte.
  appOpened: (source: "push" | "direkt" | "zurueck") =>
    trackEvent("app_open", "app", { source }),
  livePopupOpened: (state: string, quelle: "balken" | "push" | "glocke" | "link" | "auto" = "balken") =>
    trackEvent("live_popup_opened", "app", { state, quelle }),
  /** Live-Journey (Szene im Pop-up) geöffnet: Zustand, Szene, ob Routen-Kontext da ist; bei der
   *  Gassi-Welt (szene "world", 15.10.2026) zusätzlich die Etappe (abholung … ende). */
  liveJourneyOpened: (state: string, szene: string, mitKontext: boolean, etappe?: string) =>
    trackEvent("live_journey_opened", "app", { state, szene, mit_kontext: mitKontext, ...(etappe ? { etappe } : {}) }),
  /** Mitfahrer im Bulli gesehen: Anzahl Hunde, davon mit Bild (keine Namen, keine Referenzen). */
  liveJourneyMitfahrerGesehen: (anzahl: number, mitBild: number, state: string) =>
    trackEvent("live_journey_mitfahrer_gesehen", "app", { anzahl, mit_bild: mitBild, state }),
  /** AL-2 Bulli live: Entfernung sichtbar (Alter der Position als Stufe, nie die Entfernung selbst). */
  liveFahrzeugGezeigt: (state: string, schritt: "0-2" | "2-6" | "6-12") =>
    trackEvent("live_fahrzeug_gezeigt", "app", { state, quelle: "autoaid", schritt }),
  /** E6 F3: Übergabe-Foto der Rückgabe gesehen (Live-Ansicht) bzw. auf Heute angetippt. Kein Foto, keine ID. */
  rueckgabeFotoAngesehen: (quelle: "live" | "heute", state: string) =>
    trackEvent("rueckgabe_foto_angesehen", "app", { quelle, state }),
  /** AL-2 Bulli live: Entfernung im offenen Pop-up verschwunden. */
  liveFahrzeugAusgeblendet: (state: string, schritt: "angekommen" | "zu_alt" | "fenster_ende") =>
    trackEvent("live_fahrzeug_ausgeblendet", "app", { state, schritt }),
  /** Opt-out „Mein Hund im Rudel zeigen“ im Konto geändert. */
  rudelSichtbarkeitChanged: (wert: "an" | "aus") =>
    trackEvent("rudel_sichtbarkeit_changed", "app", { wert, quelle: "konto" }),
  chatMessageSent: () =>
    trackEvent("chat_message_sent", "app", {}),
  /** Sichtbarer Fehler in einem App-Bereich (Fehlerseite bzw. Chat nicht gesendet), für die Pilot-Checks. */
  uiFehler: (bereich: "hund_tab" | "heute" | "chat_senden") =>
    trackEvent("ui_fehler", "app", { szene: bereich }),
  /** CO-5: Foto oder Sprachnachricht im App-Chat gesendet. */
  chatMediaSent: (format: "foto" | "sprache") =>
    trackEvent("chat_media_sent", "app", { format }),
  /** CO-5: Kunde reagiert mit Emoji auf eine Nachricht. */
  chatReaction: () =>
    trackEvent("chat_reaction", "app", {}),
  /** Erklärung vor dem System-Dialog sichtbar (Nenner der Opt-in-Quote, T2). */
  pushPromptShown: (quelle: PushFrageQuelle) =>
    trackEvent("push_prompt_shown", "app", { quelle }),
  /** quelle: wo gefragt wurde (KA-33): erststart / live_popup / konto. */
  pushPermission: (result: "granted" | "denied", quelle: PushFrageQuelle) =>
    trackEvent("push_permission", "app", { result, quelle }),
  /** Stand der Push-Erlaubnis auf dem Gerät, nur bei Änderung (Diagnose: warum kam die Frage nicht). */
  /** diagnose nur bei token_speichern_fehler: wert = Fehlerart (http_401, netz, keine_sitzung …), anzahl = Versuche. */
  pushStatus: (status: string, erklaert: boolean, diagnose?: { wert?: string; anzahl?: number }) =>
    trackEvent("push_status", "app", { status, erklaert, ...(diagnose ?? {}) }),
  // Erster App-Start (KA-33).
  appWelcomeShown: (variante: string, rolle: string) =>
    trackEvent("app_welcome_shown", "app", { variante, rolle }),
  /** familie: geteilt/kopiert/abgebrochen = Ergebnis des Teilens (seit 16.10.2026, vorher nur „erzeugt“). */
  appWelcomeStep: (schritt: "push" | "familie" | "statistik", aktion: AppWelcomeAktion) =>
    trackEvent("app_welcome_step", "app", { schritt, aktion }),
  appWelcomeSkipped: (schritt: string) =>
    trackEvent("app_welcome_skipped", "app", { schritt }),
  appWelcomeDone: (variante: string) =>
    trackEvent("app_welcome_done", "app", { variante }),
  householdInviteCreated: (quelle: "erststart" | "konto") =>
    trackEvent("household_invite_created", "app", { quelle }),
  /** Offener Link erneut geteilt statt neu erzeugt (02.11.2026). */
  householdInviteReused: () =>
    trackEvent("household_invite_reused", "app", { quelle: "konto" }),
  /** dauer_s = Sekunden seit dem Erzeugen (Kennzahl für versehentliches Zurückziehen). */
  householdInviteRevoked: (dauerS: number) =>
    trackEvent("household_invite_revoked", "app", { quelle: "konto", dauer_s: dauerS }),
  /** Eigene Einwilligung „Erinnerungen und Tipps“ (KA-33/KA-28). */
  marketingPushConsent: (wert: "ja" | "nein", quelle: PushFrageQuelle) =>
    trackEvent("marketing_push_consent", "app", { wert, quelle }),
  /** WhatsApp-Weiche B6: „Fotos und Walk-Updates zusätzlich per WhatsApp“ an/aus. */
  whatsappWunschGeaendert: (state: "an" | "aus") =>
    trackEvent("whatsapp_wunsch_geaendert", "app", { state, quelle: "einstellungen" }),
  /** DSGVO P03: Einwilligung „Angebote per WhatsApp“ (Nachweis in wa_marketing_consent_log). */
  waWerbungConsent: (wert: "ja" | "nein", quelle: "telefon_verifizierung" | "onboarding" | "konto") =>
    trackEvent("wa_werbung_consent", "app", { wert, quelle }),
  /** Onboarding (04.11.2026): Passwort gesetzt. */
  kontoAktiviert: (flow: "gassi" | "huta") => trackEvent("konto_aktiviert", "app", { flow }),
  kontaktSchrittView: (flow: "gassi" | "huta") => trackEvent("kontakt_schritt_view", "app", { flow }),
  kontaktSchrittFertig: (flow: "gassi" | "huta") => trackEvent("kontakt_schritt_fertig", "app", { flow }),
  telefonVerifiziert: (quelle: "dialog" | "kontakt_schritt") => trackEvent("telefon_verifiziert", "app", { quelle }),
  adresseGespeichert: (quelle: "dialog" | "kontakt_schritt") => trackEvent("adresse_gespeichert", "app", { quelle }),
  agbAkzeptiert: () => trackEvent("agb_akzeptiert", "app", { flow: "gassi", quelle: "kontakt_schritt" }),
  /** Push angetippt (1.11): seit 1.10 mit event_id/event_type, seit T2 auch kategorie/nudge_key aus dem Payload. */
  pushOpened: (bezug: PushBezug) =>
    trackEvent("push_opened", "app", bezug),
  /** Tap auf eine Mitteilung in der Glocke (T2), getrennt von push_opened. */
  notificationOpened: (eventId: string | null, kategorie: string | null) =>
    trackEvent("notification_opened", "app", {
      ...(eventId ? { event_id: eventId } : {}),
      ...(kategorie ? { kategorie } : {}),
    }),
  /** Kundenausweis: Wallet-Button angetippt. „Hinzugefügt“ zählt serverseitig wallet_pass_registrations (Wallet v2). */
  walletAddTapped: (platform: "ios") =>
    trackEvent("wallet_add_tapped", "app", { platform }),
  /** Kundenausweis: Ausgang nach dem Wallet-Knopf (nativ ab iOS 1.0.3, sonst Browser-Weg nur „opened“). */
  walletAddResult: (result: "added" | "cancelled" | "error" | "opened", weg: "nativ" | "browser", status?: string) =>
    trackEvent("wallet_add_result", "app", { result, weg, ...(status ? { status } : {}) }),
  /** Tap auf das Startbildschirm-Widget (Store-Build 1.0.1). */
  widgetTapped: (state: WidgetZiel, format: WidgetGroesse) =>
    trackEvent("widget_tapped", "app", { state, format }),
  /** Team-Modus der App gewechselt (OA-5, Katalog team_modus_gewechselt). */
  teamModusGewechselt: (state: "team" | "kunde", quelle: "umschalter" | "knopf" | "mehr") =>
    trackEvent("team_modus_gewechselt", "team", { state, quelle }),
  /** Check-in/-out am HuTa-Counter gespeichert (OA-5, Katalog team_checkin_erfasst). */
  teamCheckinErfasst: (state: "check_in" | "check_out", quelle: "qr" | "manuell") =>
    trackEvent("team_checkin_erfasst", "team", { state, quelle }),
  bookingStepViewed: (flow: string, schritt: string) =>
    trackEvent("booking_step_viewed", "booking", { flow, schritt }),
  referralLinkShared: () =>
    trackEvent("referral_link_shared", "referral", {}),
  referralLinkCopied: () =>
    trackEvent("referral_link_copied", "referral", {}),
};

/** Bezug eines Pushs aus dem Payload (Verteiler: event_id/event_type/kategorie, Nudges: nudge_key). */
export type PushBezug = { event_id?: string; event_type?: string; kategorie?: string; nudge_key?: string; mit_bild?: boolean };

const BEZUG_FELDER = ["event_id", "event_type", "kategorie", "nudge_key"] as const;
const BEZUG_WERT = /^[a-z0-9_.:-]{1,64}$/i;

/** Nur die vier Bezugsfelder aus dem Push-Payload, nur kurze Schlüssel/IDs (nie Titel oder Text). */
export function pushBezugAusPayload(data: Record<string, unknown> | null | undefined): PushBezug {
  const out: PushBezug = {};
  if (!data) return out;
  for (const k of BEZUG_FELDER) {
    const v = data[k];
    if (typeof v === "string" && BEZUG_WERT.test(v)) out[k] = v;
  }
  // Foto im Push (Store-Build 1.0.1): der Server setzt mit_bild = "1" nur, wenn ein Bild dranhing.
  if (data.mit_bild === "1" || data.mit_bild === true) out.mit_bild = true;
  return out;
}

// --- Screens: screen_viewed / screen_left (Kundenbereich v2, Katalog src/lib/tracking/katalog.ts) ---
// Tabs und Sheets sind keine Seiten; ohne diese Ereignisse wäre „wo in der App“ unsichtbar.
// Ein Stapel: oben liegt der sichtbare Screen. Ein Sheet über einem Tab pausiert dessen Zeit
// (kein eigenes Ereignis), schließt es, läuft die Zeit des Tabs weiter. Hintergrund (App oder
// Browser-Tab) schreibt screen_left, Rückkehr screen_viewed mit quelle „zurueck“.
// Versand über den gemeinsamen Sammler oben.
type ScreenFelder = { dog_id?: string };
/** Screens mit Scrolltiefe (Tracking v3 A2, nur Heute und Hund-Tab). */
type ScrollScreen = "tab_heute" | "tab_hund";
interface ScreenEintrag {
  id: number;
  screen: ScreenKey;
  felder: ScreenFelder;
  seit: number | null;
  summeMs: number;
  /** Schon gemeldete Scroll-Meilensteine dieses Besuchs; null = Screen misst keine Scrolltiefe. */
  scroll: Set<number> | null;
}

const SCREEN_MAX_S = 1800;
const screenStapel: ScreenEintrag[] = [];
let screenZaehler = 0;

function screenPausieren(e: ScreenEintrag | undefined) {
  if (e && e.seit !== null) { e.summeMs += Date.now() - e.seit; e.seit = null; }
}

function screenVerlassen(e: ScreenEintrag) {
  screenPausieren(e);
  void trackEvent("screen_left", "screen", { screen: e.screen, dauer_s: Math.min(SCREEN_MAX_S, Math.round(e.summeMs / 1000)), ...e.felder });
  e.summeMs = 0;
}

function screensImHintergrund() {
  const oben = screenStapel[screenStapel.length - 1];
  if (oben && oben.seit !== null) screenVerlassen(oben);
}

function screensZurueck() {
  const oben = screenStapel[screenStapel.length - 1];
  if (oben && oben.seit === null) {
    oben.seit = Date.now();
    // Rückkehr = neuer Screen-Besuch (screen_viewed zurueck), Scroll-Meilensteine zählen neu.
    if (oben.scroll) oben.scroll.clear();
    void trackEvent("screen_viewed", "screen", { screen: oben.screen, quelle: "zurueck", ...oben.felder });
  }
}

// --- Scrolltiefe: scroll_depth (Tracking v3 A2, nur tab_heute / tab_hund in der App) ---
// Ein passiver Scroll-Listener am Fenster (die Tabs scrollen das Fenster), höchstens ein Lauf je
// Bild. Gemessen wird nur, wenn der oberste Screen des Stapels Scrolltiefe misst und sichtbar ist
// (ein Sheet darüber pausiert die Messung wie die Zeit). Je Screen-Besuch jeder Meilenstein einmal.
// Gleiche Rechnung wie der Web-Tracker (interactionsLogik.scrollTiefe), hier inline, damit kein
// geteiltes Mini-Modul in die Start-Ladekette rutscht.
const SCROLL_MEILENSTEINE = [25, 50, 75, 100] as const;
let scrollLauscher = false;
let scrollGeplant = false;

function scrollPruefen(): void {
  scrollGeplant = false;
  const oben = screenStapel[screenStapel.length - 1];
  if (!oben || !oben.scroll || oben.seit === null) return;
  const y = window.scrollY;
  const fenster = window.innerHeight;
  const hoehe = document.documentElement.scrollHeight;
  if (!(y > 0) || !(hoehe > fenster)) return;
  const tiefe = Math.min(100, ((y + fenster) / hoehe) * 100);
  for (const m of SCROLL_MEILENSTEINE) {
    if (tiefe < m || oben.scroll.has(m)) continue;
    oben.scroll.add(m);
    void trackEvent("scroll_depth", "screen", { screen: oben.screen as ScrollScreen, anzahl: m });
  }
}

function scrollMessungStarten(): void {
  if (scrollLauscher || typeof window === "undefined") return;
  scrollLauscher = true;
  window.addEventListener("scroll", () => {
    if (scrollGeplant) return;
    scrollGeplant = true;
    requestAnimationFrame(scrollPruefen);
  }, { passive: true });
}

/**
 * Screen öffnen (Tab, Sheet, Vollbild). Gibt die Schließ-Funktion zurück (für useEffect-Cleanup).
 * Felder nur aus dem Katalog: screen, quelle, dog_id. Nie Namen oder Inhalte.
 * `scrollTiefe`: scroll_depth messen (nur tab_heute / tab_hund, Katalog).
 */
export function screenOeffnen(
  screen: ScreenKey,
  quelle: ScreenQuelle,
  felder: ScreenFelder = {},
  optionen: { scrollTiefe?: boolean } = {},
): () => void {
  sammlerStarten();
  screenPausieren(screenStapel[screenStapel.length - 1]);
  const sichtbar = typeof document === "undefined" || document.visibilityState !== "hidden";
  const scrollMessen = !!optionen.scrollTiefe && (screen === "tab_heute" || screen === "tab_hund");
  if (scrollMessen) scrollMessungStarten();
  const eintrag: ScreenEintrag = {
    id: ++screenZaehler, screen, felder, seit: sichtbar ? Date.now() : null, summeMs: 0, scroll: scrollMessen ? new Set() : null,
  };
  screenStapel.push(eintrag);
  if (sichtbar) void trackEvent("screen_viewed", "screen", { screen, quelle, ...felder });
  return () => {
    const i = screenStapel.findIndex((s) => s.id === eintrag.id);
    if (i < 0) return;
    const warOben = i === screenStapel.length - 1;
    // Im Hintergrund schon geschlossen (seit null, summe 0): kein zweites screen_left.
    if (eintrag.seit !== null || eintrag.summeMs > 0) screenVerlassen(eintrag);
    screenStapel.splice(i, 1);
    const neuOben = screenStapel[screenStapel.length - 1];
    if (warOben && neuOben && neuOben.seit === null && document.visibilityState !== "hidden") neuOben.seit = Date.now();
  };
}

/**
 * Rennen bei der Öffnungsquelle (T2): Kommt der Push-Listener beim Kaltstart erst nach dem
 * app_open, steht dieses Ereignis noch im Puffer und wird hier auf „push“ umgeschrieben.
 * Gibt true zurück, wenn ein app_open umgeschrieben wurde.
 */
function oeffnungImPufferUmschreiben(fensterMs: number, quelle: "push" | "widget" | "live_activity"): boolean {
  const jetzt = Date.now();
  for (let i = puffer.length - 1; i >= 0; i--) {
    const z = puffer[i];
    if (z.event_name !== "app_open" && z.event_name !== "app_resumed") continue;
    if (jetzt - Date.parse(z.created_at) > fensterMs) return false;
    // app_open kennt nur push/direkt/zurueck (Zeitreihe): Widget- und Live-Activity-Tap färben nur
    // app_resumed (Kaltstart über die Live Activity zählt widget_tapped live im Brain).
    if (z.event_name === "app_open") {
      if (quelle !== "push") return false;
      z.event_data = { ...z.event_data, source: "push" };
    } else {
      z.event_data = { ...z.event_data, quelle };
    }
    return true;
  }
  return false;
}

// --- usePageTracking ---
// Verweildauer = Vordergrundzeit auf der Seite. Routenwechsel schließt die vorige Seite über
// den Supabase-Client. Die letzte Seite (Tab zu, App in den Hintergrund) schreibt beim
// Wechsel in den Hintergrund ihren aktuellen Stand per PATCH mit Nutzer-Token (T0); kommt der
// Nutzer zurück, läuft die Uhr weiter und ein späterer Abschluss überschreibt den Wert.
export function usePageTracking() {
  const location = useLocation();
  const currentPageViewId = useRef<string | null>(null);
  const uhr = useRef<Vordergrunduhr>(new Vordergrunduhr(Date.now(), seiteSichtbar()));
  const zuletztGeschrieben = useRef<number>(0);

  const closePreviousPage = useCallback(async () => {
    if (!currentPageViewId.current) return;
    const duration = uhr.current.sekunden(Date.now());
    try {
      await (supabase.from("page_views") as any)
        .update({
          left_at: new Date().toISOString(),
          duration_seconds: duration,
        })
        .eq("id", currentPageViewId.current);
    } catch (e) {
      console.error("[Tracking] closePreviousPage error:", e);
    }
    currentPageViewId.current = null;
  }, []);

  useEffect(() => {
    tokenBeobachten();
    let cancelled = false;

    const record = async () => {
      // Close previous page
      await closePreviousPage();

      const userId = await getUserId();
      if (!userId || cancelled) return;

      uhr.current = new Vordergrunduhr(Date.now(), seiteSichtbar());
      zuletztGeschrieben.current = 0;

      try {
        const { data } = await (supabase.from("page_views") as any)
          .insert({
            user_id: userId,
            page_path: location.pathname,
            page_title: document.title,
            referrer: document.referrer || null,
            session_id: getSessionId(),
            device_type: getDeviceType(),
            user_agent: navigator.userAgent,
            ...(await plattformFelder()),
          })
          .select("id")
          .single();

        if (data && !cancelled) {
          currentPageViewId.current = data.id;
        }
      } catch (e) {
        console.error("[Tracking] page view insert error:", e);
      }
    };

    const abbrechen = nachAktivierung(() => {
      record();
    });

    return () => {
      cancelled = true;
      abbrechen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Hintergrund / Seite schließen: Stand der aktuellen Seite schreiben (statt sendBeacon ohne Auth).
  useEffect(() => {
    const stillSchreiben = () => {
      const jetzt = Date.now();
      uhr.current.pause(jetzt);
      const id = currentPageViewId.current;
      const duration = uhr.current.sekunden(jetzt);
      if (!id || duration === zuletztGeschrieben.current) return;
      zuletztGeschrieben.current = duration;
      abschlussSenden({
        tabelle: "page_views",
        spalte: "id",
        wert: id,
        felder: { left_at: new Date(jetzt).toISOString(), duration_seconds: duration },
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stillSchreiben();
      else uhr.current.weiter(Date.now());
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", stillSchreiben);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", stillSchreiben);
    };
  }, []);
}

// --- useSessionTracking ---
// Sitzungsdauer = Vordergrundzeit seit Sitzungsstart (App: seit App-Start, Web: seit Laden
// der Seite). Bei jedem Wechsel in den Hintergrund wird der aktuelle Stand per PATCH mit
// Nutzer-Token geschrieben (T0); die Zeile wächst also mit, ended_at = letzter Hintergrund.
export function useSessionTracking() {
  const uhr = useRef<Vordergrunduhr>(new Vordergrunduhr(Date.now(), seiteSichtbar()));
  const zuletztGeschrieben = useRef<number>(0);

  useEffect(() => {
    tokenBeobachten();
    let cancelled = false;

    const startSession = async () => {
      const userId = await getUserId();
      if (!userId || cancelled) return;

      const sessionId = getSessionId();

      try {
        await (supabase.from("user_sessions") as any)
          .upsert(
            {
              user_id: userId,
              session_id: sessionId,
              device_type: getDeviceType(),
              user_agent: navigator.userAgent,
              ...(await plattformFelder()),
            },
            { onConflict: "session_id", ignoreDuplicates: true }
          );
      } catch (e) {
        console.error("[Tracking] session insert error:", e);
      }
    };

    // Die Session beginnt, wenn die Seite sichtbar wird, nicht wenn Chrome sie im
    // Hintergrund vorbereitet; sonst zählt die Prerender-Zeit als Verweildauer.
    const abbrechen = nachAktivierung(() => {
      uhr.current = new Vordergrunduhr(Date.now(), seiteSichtbar());
      startSession();
    });

    const closeSession = () => {
      const jetzt = Date.now();
      uhr.current.pause(jetzt);
      const duration = uhr.current.sekunden(jetzt);
      // visibilitychange und pagehide feuern oft beide: gleicher Stand wird nur einmal geschrieben.
      if (duration === zuletztGeschrieben.current) return;
      zuletztGeschrieben.current = duration;
      abschlussSenden({
        tabelle: "user_sessions",
        spalte: "session_id",
        wert: getSessionId(),
        felder: { ended_at: new Date(jetzt).toISOString(), duration_seconds: duration },
      });
    };

    // visibilitychange feuert zuverlässig bei Tab-/App-Wechsel (auch in der nativen App),
    // pagehide zusätzlich für iOS Safari. beforeunload feuert in der App nie.
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") closeSession();
      else uhr.current.weiter(Date.now());
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", closeSession);

    return () => {
      cancelled = true;
      abbrechen();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", closeSession);
    };
  }, []);
}

// --- useAppOpenTracking (nur native App) ---
// Zählt „App geöffnet“: beim Start und wenn die App nach mehr als 30 Minuten im Hintergrund
// wieder nach vorne kommt. Quelle „push“, wenn die Öffnung über eine Push-Benachrichtigung kam
// (src/lib/push.ts ruft merkeOeffnungUeberPush). Merkzeichen nur im Arbeitsspeicher (Datenschutz,
// 08.10.2026). Läuft nach Leerlauf, nie vor dem ersten Bild.
//
// Rennen (T2, 09.10.2026): Beim Kaltstart über einen Push kommt das Tippen-Ereignis erst, wenn
// PushSetup (nachgeladen) den Listener registriert, oft nach dem app_open. Deshalb:
// 1) app_open beim Kaltstart erst nach Leerlauf + kurzer Wartezeit schreiben,
// 2) kommt das Tippen später, wird ein app_open der letzten 20 s im Puffer auf „push“ umgeschrieben,
// 3) ein Merker ohne passendes app_open verfällt nach 20 s (Tippen im Vordergrund färbt nicht
//    das nächste app_open).
//
// app_resumed (Tracking v3 A2, 16.10.2026): Rückkehr nach mindestens 60 s und höchstens 30 Min.
// im Hintergrund (darüber bleibt es app_open zurueck, darunter zählt es zum selben Besuch).
// Quelle push/widget/direkt nach demselben Muster: Merker im Arbeitsspeicher, kommt das Tippen
// erst nach dem Ereignis, wird es im Puffer umgeschrieben. Pause nur als Stufe, nie in Sekunden.
const ZURUECK_NACH_MS = 30 * 60 * 1000;
const RUECKKEHR_AB_MS = 60 * 1000;
const RUECKKEHR_STUFE_MS = 5 * 60 * 1000;
const PUSH_FENSTER_MS = 20_000;
const KALTSTART_WARTEN_MS = 2_500;
let pushMerkerBis = 0;
let widgetMerkerBis = 0;
let widgetMerkerQuelle: "widget" | "live_activity" = "widget";

export function merkeOeffnungUeberPush(): void {
  if (oeffnungImPufferUmschreiben(PUSH_FENSTER_MS, "push")) return;
  pushMerkerBis = Date.now() + PUSH_FENSTER_MS;
}

/** Tap auf das Startbildschirm-Widget bzw. die Live Activity (src/lib/nativeEvents.ts): färbt ein
 *  app_resumed als „widget“ bzw. „live_activity“. */
export function merkeOeffnungUeberWidget(quelle: "widget" | "live_activity" = "widget"): void {
  if (oeffnungImPufferUmschreiben(PUSH_FENSTER_MS, quelle)) return;
  widgetMerkerBis = Date.now() + PUSH_FENSTER_MS;
  widgetMerkerQuelle = quelle;
}

function oeffnungsQuelle(fallback: "direkt" | "zurueck"): "push" | "direkt" | "zurueck" {
  const q = Date.now() < pushMerkerBis;
  pushMerkerBis = 0;
  widgetMerkerBis = 0;
  return q ? "push" : fallback;
}

function rueckkehrQuelle(): RueckkehrQuelle {
  const jetzt = Date.now();
  const q: RueckkehrQuelle = jetzt < pushMerkerBis ? "push" : jetzt < widgetMerkerBis ? widgetMerkerQuelle : "direkt";
  pushMerkerBis = 0;
  widgetMerkerBis = 0;
  return q;
}

function imLeerlauf(fn: () => void): void {
  const w = window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void };
  if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 5000 });
  else setTimeout(fn, 1500);
}

export function useAppOpenTracking() {
  const hiddenSeit = useRef<number | null>(null);

  useEffect(() => {
    imLeerlauf(() => {
      setTimeout(() => { void TrackingEvents.appOpened(oeffnungsQuelle("direkt")); }, KALTSTART_WARTEN_MS);
    });

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenSeit.current = Date.now();
        return;
      }
      const seit = hiddenSeit.current;
      hiddenSeit.current = null;
      if (seit === null) return;
      const pause = Date.now() - seit;
      if (pause > ZURUECK_NACH_MS) {
        imLeerlauf(() => { void TrackingEvents.appOpened(oeffnungsQuelle("zurueck")); });
      } else if (pause >= RUECKKEHR_AB_MS) {
        const wert: RueckkehrStufe = pause < RUECKKEHR_STUFE_MS ? "1-5min" : "5-30min";
        imLeerlauf(() => { void trackEvent("app_resumed", "app", { quelle: rueckkehrQuelle(), wert }); });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);
}
